<script lang="ts">
  import {
    ProductType,
    klaviyoCartItems,
    klaviyoCartItemNames,
    klaviyoAddToCartTracker,
    type IKlaviyoAddToCart,
  } from "$lib/klaviyo";

  import Icongen from "./icon-gen.svelte";
  import { goto } from "$app/navigation";
  import { utils } from "$lib/utils";
  import ErrorModal from "$components/modals/error-modal.svelte";
  import ImageComp from "$components/image.svelte";
  import { isLoading } from "$lib/store/loading-context";
  import SeatedTogether from "./seat-together.svelte";
  import type { FixtureManager } from "../_helper/fixture-manager";
  import type { ITicketBuy } from "../+page.server.ts";
  import { closeModal, openModal } from "@thehood/svlib";

  export let locale: string;
  export let fixtureManager: FixtureManager;
  export let ticketToBuy: ITicketBuy;
  export let toggleTicketBuy: boolean;
  export let toggleTicketBuyFn: () => void;

  let userQty: number = 0;
  let together: boolean = true;

  function closeModalFn(ev: Event) {
    ev.stopImmediatePropagation();
    toggleTicketBuyFn();
  }

  async function addToCart(ev: Event) {
    ev.stopImmediatePropagation();
    isLoading.set(true);
    try {
      const cart = await fixtureManager.addToCart(ticketToBuy.productId, userQty);
      if (cart) {
        const items = cart?.items;
        if (items.length) {
          let addedItem = items.find(
            item => item.sku == `${ticketToBuy.productId}` || item.sku.split("-")[1] == `${ticketToBuy.productId}`
          );
          if (addedItem) {
            const klaviyoCart: IKlaviyoAddToCart = {
              AddedItemProductID: addedItem.sku,
              AddedItemProductName: `${addedItem?.event?.title} ${addedItem.detail.ticketCategory.title}`,
              AddedItemCategories: [ProductType.Ticket],
              AddedItemPrice: addedItem.total,
              AddedItemQuantity: userQty,
              ItemNames: klaviyoCartItemNames(items),
              Items: klaviyoCartItems(items),
            };

            klaviyoAddToCartTracker(klaviyoCart);
          }
        }
        goto("/checkout");
      }
    } catch (err) {
      closeModal();
      openModal(ErrorModal, {
        message: `Error: ${JSON.stringify(err)}`,
        onCloseCallback: () => {
          closeModal();
        },
      });
    }
    isLoading.set(false);
  }

  let qtyArray: number[] = [];
  let customNotes = "";
  function updateComponentDetails(ticketToBuy: ITicketBuy) {
    // Qty Select options and set as first item
    qtyArray = utils.sellerPreferenceFn(ticketToBuy.sellPreference, ticketToBuy.availability);
    if (qtyArray.length && userQty !== qtyArray[0]) {
      userQty = qtyArray[0];
    }
    // Custom Notes
    customNotes = ticketToBuy?.customNotes?.[locale] || "";
    if (ticketToBuy.singleSeat) {
      together = false;
    }
  }

  function getNotes(ticketItem: ITicketBuy) {
    return [ticketItem.note, customNotes].filter(v => v).join(", ");
  }

  $: updateComponentDetails(ticketToBuy);
</script>

<section
  class="ticket-buy"
  class:active={toggleTicketBuy}
  role="button"
  tabindex="0"
  data-productid={ticketToBuy.productId}
>
  <div class="image">
    <div class="inner">
      <button on:click={closeModalFn}><i /><span class="sf-500">Back to results</span></button>
      {#if ticketToBuy?.ticketCategory?.image}
        <ImageComp image={ticketToBuy.ticketCategory.image} />
      {:else}
        <i class="default" />
      {/if}
      <div class="content">
        <p class="sf-500">
          {ticketToBuy?.ticketCategory?.title}
          {ticketToBuy?.block?.length ? `, Block ${ticketToBuy.block.map(v => v)} ` : ""}
        </p>
        <p class="sf-400">{ticketToBuy.availability} Ticket{ticketToBuy.availability > 1 ? "s" : ""} Available</p>
      </div>
    </div>
  </div>
  <div class="content-body">
    <div>
      <Icongen title={ticketToBuy.ticketFormat} />
      {#if together}
        <SeatedTogether sellPreference={ticketToBuy.sellPreference} availability={ticketToBuy.availability} />
      {/if}
      <hr />
    </div>
    <div class="notes">
      {#if ticketToBuy.note || ticketToBuy.customNotes}
        <div class="more-info">
          <span
            >Note:
            {getNotes(ticketToBuy)}
          </span>
        </div>
      {/if}
    </div>
    <div class="price grid">
      <div class="left">
        <h4 class="sf-800">Ticket Price</h4>
        <h4 class="sf-700 t-price">
          {#if ticketToBuy.offerPrice}
            <p class="pf-500" class:bar={ticketToBuy.offerPrice}>
              {utils.nt(ticketToBuy.price, locale, ticketToBuy.currency)}
            </p>
            <p class="pf-500">{utils.nt(ticketToBuy.offerPrice, locale, ticketToBuy.currency)}</p>
          {:else}
            <p class="pf-500">{utils.nt(ticketToBuy.price, locale, ticketToBuy.currency)} each</p>
          {/if}
        </h4>
        <p class="sf-300 tax">Not including fees and taxes</p>
      </div>
      <div class="right">
        <h4 class="sf-700">Quantity</h4>
        <select bind:value={userQty}>
          {#each qtyArray as item}
            <option value={item}>
              <span>
                {item} Ticket{item > 1 ? "s" : ""}
              </span>
            </option>
          {/each}
        </select>
      </div>
    </div>
    <hr />
    <div class="pf-400 text">
      <i /> Your tickets are covered by Football Ticket Pad’s Fan Protection policy, every order is 100% Guaranteed
    </div>
    <button on:click={addToCart}>Proceed</button>
  </div>
</section>

<style lang="scss">
  @use "@styles/breakpoint-mixins" as *;
  .ticket-buy {
    z-index: var(--z2);
    // Grid
    // mobile display
    position: fixed;
    right: 0;
    left: 0;
    bottom: 0;
    transition: all 0.3s;
    visibility: hidden;
    bottom: -100%;
    overflow-y: scroll;
    scrollbar-width: none; /* For Firefox */
    -ms-overflow-style: none; /* For Internet Explorer 10+ */

    @include desktop {
      z-index: var(--z0);
      border-radius: 0;
      clip-path: unset;
      background: unset;
      position: absolute;
      top: 0;
      bottom: 0;
      right: -100%;
      left: 100%;
    }

    &.active {
      visibility: visible;
      bottom: 0;
      bottom: -20px;
      @include desktop {
        right: 2em;
        left: 5%;
      }
    }

    p,
    h4 {
      margin: 0;
    }

    .image {
      .default {
        display: block;
        width: 100%;
        height: 100%;
        background: url("../_assets/ticket-category.jpg");
        background-repeat: no-repeat;
        background-position: center center;
        background-size: cover;
        border-radius: 0.8em 0.8em 0 0;
      }
      .inner {
        height: 14em;
        position: relative;
        button {
          display: flex;
          align-items: center;
          gap: 0.5em;
          background: none;
          border: none;
          position: absolute;
          text-align: left;
          top: 0.3em;
          span {
            font-size: 18px;
          }
          i {
            width: 0.8em;
            height: 1em;
            background: url("../_assets/arrow-light.svg");
            background-repeat: no-repeat;
            background-position: center center;
            background-size: cover;
          }
        }
        :global(img) {
          object-fit: cover;
          border-radius: 0.8em 0.8em 0 0;
        }
        .content {
          position: absolute;
          left: 10px;
          left: 0.625em;
          bottom: 0.625em;
          p {
            margin: 0.5em;
            color: #fff;
          }
        }
      }
    }
    .content-body {
      background: #fff;

      padding: 2em 1em;
      @include desktop {
        padding: 2em 0;
      }
      .notes {
        max-height: 10em; /* Set a fixed maximum height */
        overflow-y: auto; /* Enable vertical scrolling */
        scrollbar-width: none; /* For Firefox */
        margin-bottom: 0.5em;
        .more-info {
          color: #000;
          font-family: Nunito;
          font-weight: 700;
          line-height: 120%;
          margin-bottom: 10px;
        }
      }
      .price {
        grid-template-columns: 1fr 10em;
        align-items: start;
        p {
          margin-bottom: 0.5em;
        }
        h4 {
          margin-bottom: 0.5em;
        }

        .left {
          width: 100%;
          .t-price {
            p {
              color: #00a106;
              font-size: 0.8em;
            }

            .bar {
              text-decoration: line-through red;
            }
          }
          .tax {
            font-size: 0.8em;
          }
        }
        .right {
          display: grid;
          align-items: end;
          grid-gap: 0.5em;
          h4 {
            text-align: left;
            margin: 0;
            align-self: end;
          }
          select {
            align-self: center;
            margin: 0;
            border: none;
            outline: none;
            padding: 0;
            grid-template-rows: 1fr 2em;
          }
        }
      }
      .text {
        display: grid;
        // grid-template-columns: 1.1em 1fr;
        grid-template-columns: 1.2em 1fr;
        align-items: center;
        margin-bottom: 2.9em;
        gap: 0.5em;
        i {
          background: url("../_assets/shield.svg");
          width: 100%;
          height: 1.4em;
          background-repeat: no-repeat;
          background-position: center center;
          background-size: cover;
        }
      }

      hr {
        margin: 1.3em 0;
        border-bottom: 1px solid #ededed;
      }
    }
  }

  .ticket-buy ::-webkit-scrollbar {
    display: none; /* Hide scrollbar for WebKit browsers */
  }
</style>
